import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import { resources } from "@constants/hardcoded-strings";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ServicesServiceOfferingValidationErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { RpServiceConfigurationServiceOfferingsContainer } from "../../../../common/rp-service-configuration-service-offerings-container/rp-service-configuration-service-offerings-container";
import commonStyles from "../../verification-service-details-component.less";
import { getSelectedServiceOfferings, getUnselectedServiceOfferings } from "@utils/service-offerings/service-offerings-utils";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

type HumanIdentityTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ServicesServiceOfferingValidationErrors;
    initialRpServiceConfiguration: FormattedRpServiceConfiguration;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    assertionStatusMaps: Array<ApAssertionStatusMap>;
    assertionScoreServiceOfferingIDs: Array<string>;
    trustScoreServiceOfferingIDs: Array<string>;
    scoreServiceOfferingIDs: Array<string>
    createHeaderButton: (
        serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>,
        maxOrder: number
    ) => React.ReactNode;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const HumanIdentityTabContainer = (props: HumanIdentityTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        initialRpServiceConfiguration,
        rpServiceConfigurationState,
        assertionStatusMaps,
        assertionScoreServiceOfferingIDs,
        trustScoreServiceOfferingIDs,
        scoreServiceOfferingIDs,
        createHeaderButton,
        setRpServiceConfigurationState
    } = props;

    const humanIdentityServiceOfferingIds = useMemo(() => {
        return rpServiceConfigurationState.serviceOfferings
            .filter(so => so.category === "HUMAN" && so.serviceOfferingStatus === EntityStatusTypes.ACTIVE)
            .map(so => so.serviceOfferingID);
    }, []);

    //todo - need to verify no permissions are necessary to add/remove service offerings
    const humanIdentityServiceOfferings = rpServiceConfigurationState.serviceOfferings.filter(so => humanIdentityServiceOfferingIds.includes(so.serviceOfferingID));

    const selectedServiceOfferings = useMemo(() => {
        return getSelectedServiceOfferings(rpServiceConfigurationState.serviceOfferings, humanIdentityServiceOfferingIds);
    }, [rpServiceConfigurationState.serviceOfferings, humanIdentityServiceOfferingIds]);

    const unselectedServiceOfferings = useMemo(() => {
        return getUnselectedServiceOfferings(rpServiceConfigurationState.serviceOfferings, selectedServiceOfferings, humanIdentityServiceOfferingIds);
    }, [rpServiceConfigurationState.serviceOfferings, selectedServiceOfferings, humanIdentityServiceOfferingIds]);

    let maxOrder = 0;
    selectedServiceOfferings.forEach(so => {
        if (so.order && so.order >= maxOrder) {
            maxOrder = so.order + 1;
        }
    });

    const headerButton = createHeaderButton(unselectedServiceOfferings, maxOrder);

    return (
        <div>
            <div>
                <ContentPageHeader
                    title={resources.pages.verificationServiceDetails.tabs.services.subTabs.humanIdentity.title}
                    headerButton={headerButton}
                />
                <ContentPageBody isMaxWidth={true}>
                    <span className={commonStyles.description}>{resources.pages.verificationServiceDetails.tabs.services.subTabs.humanIdentity.description}</span>
                    <div>
                        {validationErrors.servicesError}
                        {unselectedServiceOfferings.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.services.subTabs.common.alerts.allServiceOfferingsMessage(
                                resources.pages.verificationServiceDetails.tabs.services.subTabs.humanIdentity.serviceOfferingCategory
                            )}</Alert>
                        )}
                        {selectedServiceOfferings.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.services.subTabs.common.alerts.noServiceOfferingsMessage(
                                resources.pages.verificationServiceDetails.tabs.services.subTabs.humanIdentity.serviceOfferingCategory,
                                isDisabled
                            )}</Alert>
                        )}
                        {selectedServiceOfferings.length > 0 && (
                            <RpServiceConfigurationServiceOfferingsContainer
                                isDisabled={isDisabled}
                                initialRpServiceConfiguration={initialRpServiceConfiguration}
                                selectedRpServiceConfigurationServiceOfferings={selectedServiceOfferings}
                                rpServiceConfigurationServiceOfferings={humanIdentityServiceOfferings}
                                assertionStatusMaps={assertionStatusMaps}
                                assertionScoreServiceOfferingIDs={assertionScoreServiceOfferingIDs}
                                trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                                scoreServiceOfferingIDs={scoreServiceOfferingIDs}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                setRpServiceConfigurationState={setRpServiceConfigurationState}
                            />
                        )}
                    </div>
                </ContentPageBody>
            </div>
        </div>
    );
};